import { useState } from "react";
import { Trash2, Copy, Bookmark, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DEFAULT_PAGE_SETTINGS,
  SECTION_DEFS,
  SECTION_ORDER,
  getPageSize,
} from "@/lib/elements";
import { ElementContent } from "../ElementRenderer";

import { PropRow } from "./primitives";
import { SpacingEditor, BorderEditor } from "./shared";
import { TypeEditor } from "./TypeEditor";
import { PageSettingsEditor } from "./PageSettingsEditor";
import { CustomElementsProvider, PageSettingsProvider } from "./contexts";
import { ELEMENT_TYPES } from "@/lib/elements";

/* ── Multi-Select Panel (shown when 2+ canvas elements are selected) ──
   Lists the selected items with their type/label and offers three bulk
   actions plus an "exit selection" button. Keeps the per-element editor
   out of the picture entirely — those props would conflict across items.
*/
function MultiSelectPanel({
  selectedIds,
  sections,
  onBulkDelete,
  onBulkDuplicate,
  onBulkSaveAsSnippet,
  onClearSelection,
}) {
  const selectedElements = [];
  for (const key of SECTION_ORDER) {
    for (const el of sections[key] || []) {
      if (selectedIds.includes(el.id)) selectedElements.push({ ...el, _section: key });
    }
  }

  return (
    <div className="p-4 space-y-3" data-testid="multi-select-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-blue-500 text-white text-xs font-bold"
            data-testid="multi-select-count"
          >
            {selectedIds.length}
          </span>
          <p className="text-sm font-heading font-bold text-gray-800">
            elements selected
          </p>
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6 text-gray-400 hover:text-gray-600"
          title="Clear selection"
          data-testid="multi-clear-btn"
          onClick={onClearSelection}
        >
          <X className="w-3 h-3" />
        </Button>
      </div>

      <p className="text-[10px] text-gray-400 leading-relaxed">
        Shift-click canvas elements to add or remove them from the selection.
        Bulk actions apply to all {selectedIds.length} items at once.
      </p>

      <Separator />

      {/* Action buttons */}
      <div className="space-y-1.5">
        <Button
          size="sm"
          variant="default"
          className="w-full h-8 gap-2 bg-amber-500 hover:bg-amber-600 text-white"
          data-testid="multi-save-snippet-btn"
          onClick={onBulkSaveAsSnippet}
        >
          <Bookmark className="w-3.5 h-3.5" />
          Save as Snippet ({selectedIds.length})
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="w-full h-8 gap-2"
          data-testid="multi-duplicate-btn"
          onClick={onBulkDuplicate}
        >
          <Copy className="w-3.5 h-3.5" />
          Duplicate All
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="w-full h-8 gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
          data-testid="multi-delete-btn"
          onClick={onBulkDelete}
        >
          <Trash2 className="w-3.5 h-3.5" />
          Delete All
        </Button>
      </div>

      <Separator />

      {/* Read-only list of selected element labels */}
      <div className="space-y-1" data-testid="multi-select-list">
        <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">
          In selection
        </p>
        {selectedElements.map((el, i) => (
          <div
            key={el.id}
            className="flex items-center gap-2 text-xs text-gray-600 px-2 py-1 rounded bg-gray-50"
            data-testid={`multi-select-item-${i}`}
          >
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ backgroundColor: SECTION_DEFS[el._section]?.color || "#999" }}
            />
            <span className="truncate">{el.label || ELEMENT_TYPES[el.type]?.label || el.type}</span>
            <span className="text-[9px] text-gray-300 shrink-0 ml-auto uppercase tracking-wide">
              {SECTION_DEFS[el._section]?.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Live Preview (small panel-only helper) ── */
function LivePreview({ sections, pageSettings }) {
  const ps = { ...DEFAULT_PAGE_SETTINGS, ...pageSettings };
  const size = getPageSize(ps.pageSize, ps);
  const scale = 0.33;
  const allElements = SECTION_ORDER.flatMap((key) => sections[key] || []);

  return (
    <div className="p-3 overflow-hidden">
      <p className="text-[10px] text-gray-400 mb-2 uppercase tracking-widest font-bold">
        Live Preview <span className="text-gray-300 normal-case tracking-normal">· {size.label}</span>
      </p>
      <div
        className="overflow-auto rounded border border-gray-200"
        style={{ height: "calc(100vh - 160px)" }}
      >
        <div
          style={{
            width: size.widthPx,
            minHeight: size.heightPx,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            backgroundColor: "white",
            padding: `${ps.marginTop}mm ${ps.marginRight}mm ${ps.marginBottom}mm ${ps.marginLeft}mm`,
            fontFamily: ps.fontFamily,
            fontSize: ps.fontSize,
          }}
        >
          {allElements.length === 0 ? (
            <p className="text-gray-300 text-center mt-40">Add elements to see preview</p>
          ) : (
            allElements.map((el) => <ElementContent key={el.id} element={el} />)
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Section Selector (small panel-only helper) ── */
function SectionSelector({ currentSection, onMove }) {
  return (
    <PropRow label="Section">
      <Select value={currentSection || ""} onValueChange={onMove}>
        <SelectTrigger className="h-7 text-xs" data-testid="section-selector">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SECTION_ORDER.map((key) => (
            <SelectItem key={key} value={key} className="text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: SECTION_DEFS[key].color }} />
                {SECTION_DEFS[key].label}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </PropRow>
  );
}

/* ── Main Properties Panel ── */
export function PropertiesPanel({
  element,
  elementSection,
  selectedIds = [],
  onMoveToSection,
  onUpdate,
  onDelete,
  onDuplicate,
  onSaveAsSnippet,
  onBulkDelete,
  onBulkDuplicate,
  onBulkSaveAsSnippet,
  onClearSelection,
  pageSettings,
  onUpdatePageSettings,
  sections,
  customElements,
}) {
  const [activeTab, setActiveTab] = useState("properties");
  const isMultiSelect = selectedIds.length > 1;

  return (
    <CustomElementsProvider value={customElements || []}>
    <PageSettingsProvider value={pageSettings || {}}>
    <div className="w-72 bg-white border-l border-gray-200 flex flex-col shrink-0" data-testid="properties-panel">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1">
        <div className="border-b border-gray-200 px-2 shrink-0">
          <TabsList className="h-9 w-full bg-transparent p-0">
            <TabsTrigger
              value="properties"
              className="text-xs flex-1 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-500 rounded-none"
              data-testid="properties-tab"
            >
              Properties
            </TabsTrigger>
            <TabsTrigger
              value="preview"
              className="text-xs flex-1 data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-500 rounded-none"
              data-testid="preview-tab"
            >
              Preview
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="properties" className="flex-1 overflow-hidden mt-0">
          <ScrollArea className="h-[calc(100vh-112px)]" data-testid="properties-scroll">
            {/* Inner wrapper adds bottom padding so last form field isn't hidden behind floating canvas badge / action bar. */}
            <div className="pb-32">
            {isMultiSelect ? (
              <MultiSelectPanel
                selectedIds={selectedIds}
                sections={sections}
                onBulkDelete={onBulkDelete}
                onBulkDuplicate={onBulkDuplicate}
                onBulkSaveAsSnippet={onBulkSaveAsSnippet}
                onClearSelection={onClearSelection}
              />
            ) : element ? (
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-heading font-bold text-gray-800">{element.label}</p>
                  <div className="flex gap-0.5">
                    {onSaveAsSnippet && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 text-amber-500 hover:text-amber-600"
                        title="Save as Snippet"
                        data-testid="prop-save-snippet-btn"
                        onClick={onSaveAsSnippet}
                      >
                        <Bookmark className="w-3 h-3" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-6 w-6" data-testid="prop-duplicate-btn" onClick={onDuplicate}>
                      <Copy className="w-3 h-3" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" data-testid="prop-delete-btn" onClick={onDelete}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <SectionSelector currentSection={elementSection} onMove={onMoveToSection} />
                <Separator />
                <TypeEditor element={element} onUpdate={onUpdate} />
                <Separator className="my-3" />
                <SpacingEditor props={element.props} onUpdate={onUpdate} />
                <Separator className="my-3" />
                <BorderEditor props={element.props} onUpdate={onUpdate} />
              </div>
            ) : (
              <PageSettingsEditor settings={pageSettings} onUpdate={onUpdatePageSettings} />
            )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="preview" className="flex-1 overflow-hidden mt-0">
          <LivePreview sections={sections} pageSettings={pageSettings} />
        </TabsContent>
      </Tabs>
    </div>
    </PageSettingsProvider>
    </CustomElementsProvider>
  );
}
